import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Returns `[value, setTimed]` — calling `setTimed(val)` sets the value and
 * automatically resets it back to `initial` after `durationMs` milliseconds.
 * Repeated calls restart the timer rather than stacking timeouts.
 */
export function useTimedState<T>(initial: T, durationMs: number) {
  const [value, setValue] = useState<T>(initial)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current) }, [])

  const setTimed = useCallback((val: T) => {
    setValue(val)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setValue(initial), durationMs)
  }, [initial, durationMs])

  return [value, setTimed] as const
}
